import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { usePendingMovieEdits, useReviewMovieEdit, usePendingEpisodeEdits, useReviewEpisodeEdit } from '../../hooks/useMovie';
import { Button } from '../../components/ui/button';
import { getApiErrorMessage } from '../../api/errors';

// [key on the API's Current/Proposed objects, label shown to the admin]
const TEXT_FIELDS = [
  ['Title', 'Title'],
  ['Description', 'Description'],
  ['Director', 'Director'],
  ['Cast', 'Cast'],
  ['ReleaseYear', 'Release year'],
  ['CategoryName', 'Category'],
  ['Rating', 'Rating'],
  ['ClassificationAuthority', 'Classification authority'],
  ['ContentDescriptors', 'Content descriptors'],
];

const EPISODE_FIELDS = [
  ['Title', 'Title'],
  ['Description', 'Description'],
  ['EpisodeNumber', 'Episode number'],
  ['SeasonNumber', 'Season number'],
];

// null/undefined on a Proposed value means "unchanged"; an empty string means "cleared".
const isProposed = (v) => v !== null && v !== undefined;
const display = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

function DiffRow({ label, current, proposed }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2 text-xs sm:grid-cols-[130px_1fr_1fr] sm:gap-3">
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-muted-foreground/70 line-through decoration-white/20">{current}</span>
      <span className="min-w-0 break-words text-foreground">{proposed}</span>
    </div>
  );
}

function EditCard({ edit }) {
  const [note, setNote] = useState('');
  const reviewMutation = useReviewMovieEdit();
  const { Current: current, Proposed: proposed } = edit;

  const thumbnailChanged = isProposed(proposed.ThumbnailUrl);
  const posterChanged = isProposed(proposed.PosterUrl);
  const trailerChanged = isProposed(proposed.TrailerUrl);

  const review = async (status) => {
    try {
      const { data } = await reviewMutation.mutateAsync({
        editId: edit.Id,
        status,
        note: note.trim() || undefined,
      });
      if (status === 'Rejected') toast.success('Edit rejected.');
      else if (data?.bunnyPushQueued) toast.success('Edit approved — the new trailer is uploading to Bunny.net in the background.');
      else toast.success('Edit approved and applied.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Action failed'));
    }
  };

  return (
    <div className="rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-base font-semibold text-foreground">{edit.MovieTitle}</h3>
        <p className="text-xs text-muted-foreground">
          {edit.RequestedBy ? `by ${edit.RequestedBy} · ` : ''}
          {edit.DateRequested?.split('T')[0]}
        </p>
      </div>

      <div className="mt-3 hidden grid-cols-[130px_1fr_1fr] gap-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 sm:grid">
        <span />
        <span>Current</span>
        <span>Proposed</span>
      </div>

      <div className="divide-y divide-white/[0.06]">
        {TEXT_FIELDS.filter(([key]) => isProposed(proposed[key])).map(([key, label]) => (
          <DiffRow
            key={key}
            label={label}
            current={display(current[key])}
            proposed={proposed[key] === '' ? '(cleared)' : display(proposed[key])}
          />
        ))}

        {isProposed(proposed.IsKidsContent) && (
          <DiffRow
            label="Kids content"
            current={current.IsKidsContent ? 'Yes' : 'No'}
            proposed={proposed.IsKidsContent ? 'Yes' : 'No'}
          />
        )}

        {posterChanged && (
          <div className="grid grid-cols-1 gap-2 py-2 text-xs sm:grid-cols-[130px_1fr_1fr] sm:gap-3">
            <span className="font-medium text-muted-foreground">Poster</span>
            {current.PosterUrl ? (
              <img src={current.PosterUrl} alt="Current poster" className="h-[120px] w-[80px] rounded-md object-cover opacity-70" />
            ) : (
              <span className="text-muted-foreground/70">—</span>
            )}
            <img src={proposed.PosterUrl} alt="Proposed poster" className="h-[120px] w-[80px] rounded-md object-cover" />
          </div>
        )}

        {thumbnailChanged && (
          <div className="grid grid-cols-1 gap-2 py-2 text-xs sm:grid-cols-[130px_1fr_1fr] sm:gap-3">
            <span className="font-medium text-muted-foreground">Thumbnail</span>
            {current.ThumbnailUrl ? (
              <img src={current.ThumbnailUrl} alt="Current thumbnail" className="h-[72px] w-[128px] rounded-md object-cover opacity-70" />
            ) : (
              <span className="text-muted-foreground/70">—</span>
            )}
            <img src={proposed.ThumbnailUrl} alt="Proposed thumbnail" className="h-[72px] w-[128px] rounded-md object-cover" />
          </div>
        )}

        {trailerChanged && (
          <div className="grid grid-cols-1 gap-1 py-2 text-xs sm:grid-cols-[130px_1fr_1fr] sm:gap-3">
            <span className="font-medium text-muted-foreground">Trailer</span>
            {current.TrailerUrl ? (
              <a href={current.TrailerUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:underline">
                <Icon icon="ph:play-bold" className="size-3.5" />
                Watch current
              </a>
            ) : (
              <span className="text-muted-foreground/70">—</span>
            )}
            <a href={proposed.TrailerUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline">
              <Icon icon="ph:play-bold" className="size-3.5" />
              Watch proposed
            </a>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note to the creator (shown if you reject) — optional"
          className="w-full flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50"
        />
        <div className="flex shrink-0 justify-end gap-2">
          <Button
            size="sm"
            onClick={() => review('Approved')}
            disabled={reviewMutation.isPending}
            className="gap-1.5 bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 hover:bg-emerald-500/25"
          >
            <Icon icon="ph:check-bold" />
            Approve
          </Button>
          <Button
            size="sm"
            onClick={() => review('Rejected')}
            disabled={reviewMutation.isPending}
            className="gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
          >
            <Icon icon="ph:x-bold" />
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

function EpisodeEditCard({ edit }) {
  const [note, setNote] = useState('');
  const reviewMutation = useReviewEpisodeEdit();
  const { Current: current, Proposed: proposed } = edit;
  const videoChanged = isProposed(proposed.VideoUrl);

  const review = async (status) => {
    try {
      const { data } = await reviewMutation.mutateAsync({
        editId: edit.Id,
        status,
        note: note.trim() || undefined,
      });
      if (status === 'Rejected') toast.success('Edit rejected.');
      else if (data?.bunnyPushQueued) toast.success('Edit approved — the new video is uploading to Bunny.net in the background.');
      else toast.success('Edit approved and applied.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Action failed'));
    }
  };

  return (
    <div className="rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-base font-semibold text-foreground">
          {edit.MovieTitle} <span className="text-sm font-normal text-muted-foreground">· {edit.EpisodeLabel}: {current.Title}</span>
        </h3>
        <p className="text-xs text-muted-foreground">
          {edit.RequestedBy ? `by ${edit.RequestedBy} · ` : ''}
          {edit.DateRequested?.split('T')[0]}
        </p>
      </div>

      <div className="mt-3 hidden grid-cols-[130px_1fr_1fr] gap-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 sm:grid">
        <span />
        <span>Current</span>
        <span>Proposed</span>
      </div>

      <div className="divide-y divide-white/[0.06]">
        {EPISODE_FIELDS.filter(([key]) => isProposed(proposed[key])).map(([key, label]) => (
          <DiffRow
            key={key}
            label={label}
            current={display(current[key])}
            proposed={proposed[key] === '' ? '(cleared)' : display(proposed[key])}
          />
        ))}

        {videoChanged && (
          <div className="grid grid-cols-1 gap-1 py-2 text-xs sm:grid-cols-[130px_1fr_1fr] sm:gap-3">
            <span className="font-medium text-muted-foreground">Video</span>
            {current.VideoUrl ? (
              <a href={current.VideoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:underline">
                <Icon icon="ph:play-bold" className="size-3.5" />
                Watch current
              </a>
            ) : (
              <span className="text-muted-foreground/70">—</span>
            )}
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              <a href={proposed.VideoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline">
                <Icon icon="ph:play-bold" className="size-3.5" />
                Watch proposed
              </a>
              <a href={proposed.VideoUrl} download className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline">
                <Icon icon="ph:download-bold" className="size-3.5" />
                Download
              </a>
            </span>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note to the creator (shown if you reject) — optional"
          className="w-full flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50"
        />
        <div className="flex shrink-0 justify-end gap-2">
          <Button
            size="sm"
            onClick={() => review('Approved')}
            disabled={reviewMutation.isPending}
            className="gap-1.5 bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 hover:bg-emerald-500/25"
          >
            <Icon icon="ph:check-bold" />
            Approve
          </Button>
          <Button
            size="sm"
            onClick={() => review('Rejected')}
            disabled={reviewMutation.isPending}
            className="gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
          >
            <Icon icon="ph:x-bold" />
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

function PendingEpisodeEdits() {
  const { data: edits = [], isLoading, error } = usePendingEpisodeEdits();

  if (isLoading) return null;

  if (error) {
    return (
      <p className="mt-8 text-xs text-destructive">
        {getApiErrorMessage(error, 'Failed to load pending episode edits.')}
      </p>
    );
  }

  if (edits.length === 0) return null;

  return (
    <>
      <h2 className="mb-2.5 mt-8 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
        <Icon icon="ph:pencil-simple-line-bold" className="size-3.5" />
        Pending episode edits ({edits.length})
      </h2>
      <div className="flex flex-col gap-3">
        {edits.map((edit) => (
          <EpisodeEditCard key={edit.Id} edit={edit} />
        ))}
      </div>
    </>
  );
}

/**
 * Edits that content creators have proposed to their own (possibly live) titles. Nothing here has
 * touched the title yet — approving applies the changes, rejecting discards them. Renders nothing
 * when there is nothing to review.
 */
function PendingTitleEdits() {
  const { data: edits = [], isLoading, error } = usePendingMovieEdits();

  if (isLoading) return null;

  if (error) {
    return (
      <p className="mt-8 text-xs text-destructive">
        {getApiErrorMessage(error, 'Failed to load pending title edits.')}
      </p>
    );
  }

  if (edits.length === 0) return null;

  return (
    <>
      <h2 className="mb-2.5 mt-8 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
        <Icon icon="ph:pencil-simple-line-bold" className="size-3.5" />
        Pending title edits ({edits.length})
      </h2>
      <div className="flex flex-col gap-3">
        {edits.map((edit) => (
          <EditCard key={edit.Id} edit={edit} />
        ))}
      </div>
    </>
  );
}

/** Everything content creators have proposed to their own titles and episodes, awaiting a ruling. */
export default function PendingMovieEdits() {
  return (
    <>
      <PendingTitleEdits />
      <PendingEpisodeEdits />
    </>
  );
}
